import useParentsProps from "@hooks/useParentsProps";

import { router, usePage } from "@inertiajs/react";

import { CheckoutFlowEnum } from "@ts/enums/CheckoutFlowEnum";
import { CheckoutPagesEnum } from "@ts/enums/CheckoutPagesEnum";
import { StateSlicesEnum } from "@ts/enums/StateSlicesEnum";
import { License } from "@ts/interfaces/Checkout/SubjectInterface";
import { CheckoutStateInterface } from "@ts/interfaces/Misc/state/CheckoutStateInterface";
import { ParentsStateInterface } from "@ts/interfaces/Misc/state/ParentsStateInterface";
import { PremiumServiceInterface } from "@ts/interfaces/Parent/PremiumServiceInterface";
import { UserInterface } from "@ts/interfaces/Parent/UserInterface";

import { updateAppStateApi } from "@api/stateApi";

import useResetAtomicState from "./useResetAtomicState";

const useAppStateSync = (): {
    updateAppState: <T>(
        sliceKey: StateSlicesEnum,
        state: T,
        navigateTo?: string
    ) => Promise<void>;
    constructCheckoutAppState: (
        data: CheckoutStateInterface
    ) => CheckoutStateInterface;
    updateFlows: {
        resetCheckoutAppState: (
            navigateTo?: string,
            data?: CheckoutStateInterface
        ) => void;
        updateAppStateForAddNewStudent: () => void;
        updateAppStateForRenewStudent: (student: UserInterface) => void;
        updateAppStateForAddService: (
            student: UserInterface,
            services: {
                active: PremiumServiceInterface[];
                cancelled: string[];
            }
        ) => void;
        updateAppStateForSubjectPayment: (
            student: UserInterface,
            license: License,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            subjects: any,
            subjectIds: number[],
            pathway: string,
            subjectChangeFeeItemPriceId: string,
            subjectChangeFeeItemPrice: number,
            changedSubjects: number
        ) => void;
    };
    state: {
        checkout: CheckoutStateInterface | null;
        parents: ParentsStateInterface | null;
    };
} => {
    const {
        app_state: {
            checkout_state: checkoutState,
            parents_state: parentsState
        }
    } = usePage().props as unknown as {
        app_state: {
            checkout_state: CheckoutStateInterface | null;
            parents_state: ParentsStateInterface | null;
        };
    };

    const { parentUser } = useParentsProps();

    const { resetCheckoutAtomicState } = useResetAtomicState();

    const updateAppState = <T>(
        sliceKey: StateSlicesEnum,
        state: T,
        navigateTo?: string
    ): Promise<void> => {
        return new Promise<void>((resolve, reject) => {
            const handleSuccess = () => {
                resolve();
            };

            const handleFailure = (error: Error) => {
                console.error(error);
                reject(error);
            };

            if (navigateTo) {
                router.patch(
                    route("state.update"),
                    {
                        slice_key: sliceKey,
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        state: state as any,
                        navigate_to: navigateTo
                    },
                    {
                        onError(e) {
                            handleFailure(e as unknown as Error);
                        },
                        onSuccess() {
                            handleSuccess();
                        }
                    }
                );
            } else {
                updateAppStateApi<T>({
                    slice_key: sliceKey.toString(),
                    state
                })
                    .then(data => {
                        console.log(data.data);
                        handleSuccess();
                    })
                    .catch(e => {
                        console.error(e);
                        handleFailure(e);
                    });
            }
        });
    };

    const constructCheckoutAppState = (data: CheckoutStateInterface) => {
        const state: CheckoutStateInterface = {
            checkoutRedirectsTo: null,
            checkoutRedirectsToOnBackNav: null,
            checkoutStudentId: null,
            checkoutStudentCBId: null,
            addonEntitlementsIds: null,
            checkoutPurchasingAddon: null,
            checkoutSubjectsFee: null,
            hasPendingSubCreated: false,
            checkoutUser: {
                receiveOfferNews: false,
                firstName: parentUser!.firstName,
                email: parentUser!.email,
                nextAttemptPage: CheckoutPagesEnum.USER_INFO_PAGE
            },
            ...data
        };
        return state;
    };

    const resetCheckoutAppState = (
        navigateTo?: string,
        data?: CheckoutStateInterface
    ) => {
        updateAppState<CheckoutStateInterface>(
            StateSlicesEnum.CHECKOUT,
            constructCheckoutAppState({ checkoutFlow: null, ...data }),
            navigateTo
        ).catch(e => console.log(e));
    };

    const updateAppStateForAddNewStudent = () => {
        resetCheckoutAtomicState({ forceResetCheckoutAtom: true });
        updateAppState<CheckoutStateInterface>(
            StateSlicesEnum.CHECKOUT,
            constructCheckoutAppState({
                checkoutFlow: CheckoutFlowEnum.ADD_STUDENT,
                checkoutRedirectsTo: route("parents.manage-programs"),
                checkoutUser: {
                    receiveOfferNews: false,
                    firstName: parentUser!.firstName,
                    email: parentUser!.email,
                    nextAttemptPage: CheckoutPagesEnum.STATE_SELECT_PAGE
                }
            }),
            // TODO: Improve hardcorded check for contries don't need state selections
            parentUser?.countryId === "VN"
                ? route("checkout.select-age-group")
                : route("checkout.select-state")
        ).catch(e => console.log(e));
    };

    const updateAppStateForRenewStudent = (student: UserInterface) => {
        resetCheckoutAtomicState({ forceResetCheckoutAtom: true });
        updateAppState<CheckoutStateInterface>(
            StateSlicesEnum.CHECKOUT,
            constructCheckoutAppState({
                checkoutFlow: CheckoutFlowEnum.PROGRAM_RENEWAL,
                checkoutRedirectsTo: route("parents.manage-student", {
                    student: student.id
                }),
                checkoutStudentId: student.id,
                checkoutUser: {
                    receiveOfferNews: false,
                    firstName: parentUser!.firstName,
                    email: parentUser!.email,
                    nextAttemptPage: CheckoutPagesEnum.STATE_SELECT_PAGE
                }
            }),
            // TODO: Improve hardcorded check for contries don't need state selections
            parentUser?.countryId === "VN"
                ? route("checkout.select-age-group")
                : route("checkout.select-state")
        ).catch(e => console.log(e));
    };

    const updateAppStateForAddService = (
        student: UserInterface,
        services: {
            active: PremiumServiceInterface[];
            cancelled: string[];
        }
    ) => {
        resetCheckoutAtomicState({ forceResetCheckoutAtom: true });
        updateAppState<CheckoutStateInterface>(
            StateSlicesEnum.CHECKOUT,
            constructCheckoutAppState({
                checkoutFlow: CheckoutFlowEnum.PREMIUM_SERVICE_PAYMENT,
                checkoutRedirectsTo: route("parents.manage-student", {
                    student: student.id
                }),
                checkoutUser: {
                    receiveOfferNews: false,
                    firstName: parentUser!.firstName,
                    email: parentUser!.email,
                    nextAttemptPage: CheckoutPagesEnum.STATE_SELECT_PAGE
                },
                addonEntitlementsIds: services.active.map(
                    p => p.entitlement_id
                ),
                checkoutStudentId: student.id,
                checkoutStudentCBId: student.chargebee_id,
                checkoutPurchasingAddon: {
                    productFamilyId: student.latest_license.plan.families.cb_id,
                    grade: student.student_grade.long_name,
                    planId: student.latest_license.plan.cb_id
                }
            }),
            // TODO: Improve hardcorded check for contries don't need state selections
            parentUser?.countryId === "VN"
                ? route("checkout.select-age-group")
                : route("checkout.select-state")
        ).catch(e => console.log(e));
    };

    const updateAppStateForSubjectPayment = (
        student: UserInterface,
        license: License,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        subjects: any,
        subjectIds: number[],
        pathway: string,
        subjectChangeFeeItemPriceId: string,
        subjectChangeFeeItemPrice: number,
        changedSubjects: number
    ) => {
        updateAppState<CheckoutStateInterface>(
            StateSlicesEnum.CHECKOUT,
            constructCheckoutAppState({
                checkoutRedirectsTo: route(
                    "parents.success-subject-selection",
                    {
                        student: student?.id,
                        license: license?.id
                    }
                ),
                checkoutFlow: CheckoutFlowEnum.SUBJECT_CHANGE_FEE_PAYMENT,
                checkoutRedirectsToOnBackNav: route(
                    "parents.evaluate-changed-subjects",
                    {
                        student: student.id,
                        license: license?.id,
                        selectedSubjects: JSON.stringify(subjects)
                    }
                ),
                checkoutStudentId: student.id,
                checkoutStudentCBId: student.chargebee_id,
                checkoutSubjectsFee: {
                    priceId: subjectChangeFeeItemPriceId!,
                    price: subjectChangeFeeItemPrice!,
                    quantity: changedSubjects!,
                    grade: license?.grade?.long_name,
                    planId: license.plan.cb_id,
                    productFamilyId: license.plan.families.cb_id,
                    subjectIds,
                    pathway,
                    selectedLicense: license
                },
                checkoutUser: {
                    receiveOfferNews: false,
                    firstName: parentUser!.firstName,
                    email: parentUser!.email,
                    nextAttemptPage: CheckoutPagesEnum.PAYMENT_PAGE
                }
            }),
            route("checkout.payment")
        ).catch(e => console.log(e));
    };

    return {
        updateAppState,
        constructCheckoutAppState,
        updateFlows: {
            resetCheckoutAppState,
            updateAppStateForAddNewStudent,
            updateAppStateForRenewStudent,
            updateAppStateForSubjectPayment,
            updateAppStateForAddService
        },
        state: { checkout: checkoutState, parents: parentsState }
    };
};

export default useAppStateSync;
